import { useMutation, useQuery } from '@tanstack/react-query';
import { getJson, postJson } from './http.js';
import type {
  CatalogResponse,
  CheckoutResponse,
  LotView,
  OrderView,
  TraceabilitySearch,
} from './types.js';

/** Market prices change; refetch periodically so the portal never shows a stale price for long. */
const CATALOG_REFRESH_MS = 30_000;

export function useCatalog() {
  return useQuery({
    queryKey: ['catalog'],
    queryFn: () => getJson<CatalogResponse>('/api/catalog'),
    refetchInterval: CATALOG_REFRESH_MS,
  });
}

export function useLotView(lotId: string) {
  return useQuery({
    queryKey: ['lot', lotId],
    queryFn: () => getJson<LotView>(`/api/traceability/lots/${encodeURIComponent(lotId)}`),
    enabled: lotId !== '',
    retry: false,
  });
}

/** The API rejects shorter queries: too broad to be useful. */
export const MIN_SEARCH_LENGTH = 3;

export function useTraceabilitySearch(query: string) {
  return useQuery({
    queryKey: ['traceability-search', query],
    queryFn: () =>
      getJson<TraceabilitySearch>(`/api/traceability/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= MIN_SEARCH_LENGTH,
    retry: false,
  });
}

/** Keep confirming while the payment is pending; stop after ~2 minutes (the poller takes over). */
const CONFIRM_INTERVAL_MS = 3_000;
const MAX_CONFIRM_ATTEMPTS = 40;

export function useOrderConfirmation(publicId: string) {
  return useQuery({
    queryKey: ['order', publicId],
    queryFn: () => postJson<OrderView>(`/api/orders/${encodeURIComponent(publicId)}/confirm`),
    enabled: publicId !== '',
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'AWAITING_PAYMENT' &&
      query.state.dataUpdateCount < MAX_CONFIRM_ATTEMPTS
        ? CONFIRM_INTERVAL_MS
        : false,
  });
}

export interface CheckoutRequest {
  readonly items: { readonly sku: string; readonly quantity: number }[];
  readonly buyer: { readonly name: string; readonly email: string };
}

export function useCheckout() {
  return useMutation({
    mutationFn: (request: CheckoutRequest) => postJson<CheckoutResponse>('/api/checkout', request),
  });
}
